import { preloadParryBloom } from './parry_bloom_fx'
// パリィ周りの光の作り直し (2026-10-05・全ボス共通)。弾き崩し＝A+B・受けた＝案2 (シールドなし・影なし)。
//   ユーザー裁定 10-05「シールドなしで進めて」「影はなしで」(見比べ版5 https://claude.ai/artifact/HUpCmYgDgEPCYXdt8qgyn2)。
//   見た目は撮影台の仮組み (C:\dev\LUNAIRE-notes\assets\2026-10-05-parry-family-fx\harness\proto2.ts・proto3.ts) をそのまま移した。
//   台帳 = docs/ledger/combat.md 2026-10-05。判定・数値・音は変えない (描くだけ)。
//   座標は世界座標。dir = 主人公の向き (+1 右)。仮組みは右向きで作ってあるので、左向きは左右反転して描く。
//   縛り＝線 (stroke) で描かない。面と粒で作る。
//
//   比較 = DEV の `?guardFx=now` (受けた＝今までの紫の粒)。弾き崩しの旧案は parry_break_fx.ts の `?parryBreakFx=`。

export type GuardFxLook = 'now' | 'a2' | 'img'
// 既定 = img (2026-10-06 ユーザー裁定「1はB」＝見比べ版8：光らせない・暗い鋼色の火花と砂ぼこりを画像生成の素材で・下がる距離は元の半分)。
//   10-05 の a2 (コードの粒・押し返し ×1.5) と今までの紫の粒は DEV の `?guardFx=a2|now` に残す。
let guardLook: GuardFxLook = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'img'
  const v = new URLSearchParams(window.location.search).get('guardFx')
  return v === 'now' || v === 'a2' ? v : 'img'
})()
export function setGuardFxLook(l: GuardFxLook): void { guardLook = l }
export function getGuardFxLook(): GuardFxLook { return guardLook }

/** 受けた時の描画だけの押し返しの倍率。img (10-06) は「下がる距離が長い」で元の半分・a2 (10-05) は 1.5 倍。 */
/** ガードの砂ぼこりを前の多い量で描くか (DEV ?guardDust=old だけ)。 */
let guardDustOldFlag = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('guardDust') === 'old'
})()
export function guardDustOld(): boolean { return guardDustOldFlag }
export function setGuardDustOld(old: boolean): void { guardDustOldFlag = old }

export function guardPushMul(): number { return guardLook === 'img' ? 0.5 : guardLook === 'a2' ? 1.5 : 1 }

// 崩した瞬間 (完璧な弾きでボスの体幹が尽きた時) の光。z = 見比べ版4の強さ (画面ごと金に染まる＋寄る・ぶれる＋閃光4コマ)。
//   2026-10-06 ユーザー裁定「2はA」で入れたが、実機で「崩した瞬間の演出は取り下げで。画面が光りすぎて壊れたのかとすら思うほど激しいし、
//   一瞬だから画面が光ったようにしか感じない」→ 既定は now (今まで通りの成功の光)。z は DEV の `?parryDownFx=z` に残す。
export type ParryDownLook = 'now' | 'z'
let downLook: ParryDownLook = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'now'
  return new URLSearchParams(window.location.search).get('parryDownFx') === 'z' ? 'z' : 'now'
})()
export function setParryDownLook(l: ParryDownLook): void { downLook = l }
export function getParryDownLook(): ParryDownLook { return downLook }

// 画像生成の素材 (Codex guard-spark-dust-art-1006・parry-zzz-look-1005 を切り出し、受けたの火花は暗く焼き込み済み)。黒背景＝加算で描く。
const MAT_NAMES = [0, 1, 2, 3].flatMap((i) => [`gspark_hold_${i}`, `gspark_strain_${i}`, `gdust_${i}`, `flash_just_${i}`])
const MAT: Record<string, HTMLImageElement> = {}
export function preloadParryFamilyFx(prefix = '/sprites/fx/parry-family/'): void {
  preloadParryBloom()
  if (typeof Image === 'undefined') return
  for (const n of MAT_NAMES) {
    if (MAT[n]) continue
    const img = new Image(); img.src = `${prefix}${n}.webp`; MAT[n] = img
  }
}
function matReady(base: string): boolean {
  for (let i = 0; i < 4; i++) { const m = MAT[`${base}_${i}`]; if (!m || !m.complete || !m.naturalWidth) return false }
  return true
}
// 4コマを時間で重ねる (keys[i] で i コマ目が最大・前後へ線形に溶ける)。素材上の基準点 (ax,ay) を世界座標 (x,y) に合わせて scale 倍で描く。
function drawSeqAt(ctx: CanvasRenderingContext2D, base: string, age: number, keys: number[], life: number,
  x: number, y: number, ax: number, ay: number, scale: number, alpha = 1): void {
  if (age < 0 || age > life || !matReady(base)) return
  const end = [...keys.slice(1), life]
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 4; i++) {
    const t0 = i === 0 ? -1e-3 : keys[i - 1], t1 = keys[i], t2 = end[i]
    let w = 0
    if (age >= t0 && age <= t1) w = i === 0 ? 1 : (age - t0) / Math.max(1e-6, t1 - t0)
    else if (age > t1 && age <= t2) w = 1 - (age - t1) / Math.max(1e-6, t2 - t1)
    if (w <= 0.003) continue
    const f = MAT[`${base}_${i}`]
    ctx.globalAlpha = Math.min(1, w) * alpha
    ctx.drawImage(f, x - ax * scale, y - ay * scale, f.naturalWidth * scale, f.naturalHeight * scale)
  }
  ctx.restore()
}

/** 弾き崩し・受けたの光が残る長さ (秒)。game.ts の ageFx はこの2種だけここまで残す (他の FX は 0.5 秒のまま)。 */
export const PARRY_FAMILY_FX_LIFE_SEC = 0.8

const DEG = Math.PI / 180
const clamp01 = (v: number): number => v < 0 ? 0 : v > 1 ? 1 : v
function h01(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453
  return s - Math.floor(s)
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, a: number, sx = 1, sy = 1): void {
  if (a <= 0.003 || r <= 0.5) return
  ctx.save(); ctx.translate(x, y); ctx.scale(sx, sy)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r)
  g.addColorStop(0, `rgba(${rgb},${a})`)
  g.addColorStop(0.4, `rgba(${rgb},${a * 0.45})`)
  g.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

type EmberOpts = { n: number; a0: number; a1: number; v0: number; v1: number; life: number; grav: number; drag: number;
  size: number; cols: string[]; seed: number; tailMax?: number }
// 尾を引く粒。速さの向きに伸びた涙形を面で塗る (線は使わない)。
function embers(ctx: CanvasRenderingContext2D, x0: number, y0: number, age: number, o: EmberOpts): void {
  for (let i = 0; i < o.n; i++) {
    const life = o.life * (0.55 + 0.45 * h01(o.seed + i * 3.1))
    if (age <= 0 || age >= life) continue
    const ang = o.a0 + (o.a1 - o.a0) * h01(o.seed + i * 1.3)
    const v = o.v0 + (o.v1 - o.v0) * Math.pow(h01(o.seed + i * 2.9), 1.4)
    const k = o.drag
    const dist = (v / k) * (1 - Math.exp(-k * age))
    const x = x0 + Math.cos(ang) * dist
    const y = y0 + Math.sin(ang) * dist + 0.5 * o.grav * age * age
    const vv = v * Math.exp(-k * age)
    const vx = Math.cos(ang) * vv, vy = Math.sin(ang) * vv + o.grav * age
    const sp = Math.hypot(vx, vy) || 1
    const tail = Math.min(o.tailMax ?? 9, sp * 0.016)
    const f = 1 - age / life
    const r = o.size * (0.6 + 0.6 * h01(o.seed + i * 5.3)) * (0.5 + 0.5 * f)
    const nx = -vy / sp, ny = vx / sp
    ctx.globalAlpha = Math.min(1, f * 1.4)
    ctx.fillStyle = o.cols[i % o.cols.length]
    ctx.beginPath()
    ctx.moveTo(x + nx * r, y + ny * r)
    ctx.lineTo(x - (vx / sp) * tail, y - (vy / sp) * tail)
    ctx.lineTo(x - nx * r, y - ny * r)
    ctx.arc(x, y, r, Math.atan2(-ny, -nx), Math.atan2(ny, nx))
    ctx.fill()
  }
  ctx.globalAlpha = 1
}

// 右向きで描いた物を、ax を軸に左右反転する (dir = -1 の時だけ)。
function mirrorAbout(ctx: CanvasRenderingContext2D, ax: number, dir: number): void {
  if (dir >= 0) return
  ctx.translate(ax, 0); ctx.scale(-1, 1); ctx.translate(-ax, 0)
}

// ---------- 弾き崩し A+B ----------
/** A: 画面の色が抜ける強さ 0..1 (止めの間は最大・明けてから 0.35 秒で戻る)。 */
export function parryBreakDesat(age: number): number {
  if (age < 0) return 0
  if (age < 0.04) return age / 0.04
  if (age < 0.28) return 1
  return Math.max(0, 1 - (age - 0.28) / 0.35)
}

/** 弾き崩しの色付きの光 (A の潰れた朱＋B の朱の火の粉の尾)。色の抜けた画面の上に描く。x,y = 掲げた刀身。 */
export function drawParryBreakAB(ctx: CanvasRenderingContext2D, x: number, y: number, dir: number, age: number): void {
  if (age < 0 || age > PARRY_FAMILY_FX_LIFE_SEC) return
  ctx.save()
  mirrorAbout(ctx, x, dir)
  ctx.globalCompositeOperation = 'lighter'
  // A: 剣のまわりに横へ潰れた朱だけ残る
  if (age <= 0.6) {
    const pop = age < 0.04 ? age / 0.04 : Math.max(0, 1 - (age - 0.04) / 0.5)
    glow(ctx, x, y, 34 * (0.5 + 0.5 * pop), '224,70,48', 0.9 * pop, 1.3, 0.75)
    glow(ctx, x, y, 10 * pop + 3, '255,200,170', 0.8 * pop)
  }
  // B: 剣が弾かれ、朱の火の粉が後ろ上へ弧を描いて尾を引き、床へこぼれる
  glow(ctx, x, y, 22, '235,90,60', 0.8 * Math.max(0, 1 - age / 0.12))
  embers(ctx, x, y, age, { n: 34, a0: -165 * DEG, a1: -105 * DEG, v0: 260, v1: 620, life: 0.75, grav: 1300, drag: 2.6,
    size: 2.2, cols: ['#ff6a3c', '#ff9a5a', '#e04a30', '#ffc08a'], seed: 71, tailMax: 16 })
  ctx.restore()
}

// ---------- 受けた 案2 (シールドなし) ----------
/** 受けた時の描画だけの押し返し (px・世界座標)。render.ts の guardImpactVisualPush と同じ式×guardPushMul。 */
export function guardImpactPushPx(age: number, dir: number, postureRatio: number): number {
  if (age < 0 || age >= 0.18) return 0
  const strain = postureRatio * postureRatio * (3 - 2 * postureRatio)
  return -dir * Math.sin((age / 0.18) * Math.PI) * (7 + strain * 9) * guardPushMul()
}

/**
 * 刃の当たりを強め、火花を相手側へ跳ね返し、後ろ足の後ろへ砂を出す。
 * 色は鈍い鋼の青白 (弾いた＝金/銀白・崩された＝朱 と分ける)。もう保たない時 (体幹 5 割超) は朱が混じり、砂が長く濃い。
 * x,y = player_guard_impact の点 (足元から 60px 上)。押し返しに合わせて光も一緒に下がる。
 */
export function drawGuardImpactA2(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, dir: number, postureRatio: number): void {
  if (age < 0 || age > 0.6) return
  const brk = postureRatio > 0.5
  const px = x + guardImpactPushPx(age, dir, postureRatio)
  const cx = px + 12, cy = y - 40            // 剣と相手の得物の接点 (右向きで)
  const footX = px - 16, floorY = y + 60     // 後ろ足
  ctx.save()
  mirrorAbout(ctx, px, dir)
  ctx.globalCompositeOperation = 'lighter'
  const k = Math.max(0, 1 - age / 0.07)
  glow(ctx, cx, cy, 22, '215,225,250', 0.8 * k, 0.5, 1.6)                     // 刃に沿った縦の当たり
  glow(ctx, cx + 5, cy, 44, '150,175,235', 0.42 * Math.max(0, 1 - age / 0.13), 1.1, 0.8)
  embers(ctx, cx + 3, cy, age, { n: brk ? 24 : 34, a0: -78 * DEG, a1: 22 * DEG, v0: 240, v1: 660, life: 0.42, grav: 950, drag: 4.2,
    size: 2.4, cols: brk ? ['#fff0dc', '#ff7a4a', '#e85a36', '#ffd8b0'] : ['#e6ecfa', '#bccbec', '#a2b4dc', '#f2f2f6'], seed: 47, tailMax: 14 })
  embers(ctx, cx, cy + 4, age, { n: 8, a0: 70 * DEG, a1: 115 * DEG, v0: 60, v1: 170, life: 0.5, grav: 1200, drag: 3,
    size: 1.8, cols: ['#b8c0d8', '#9aa4c0'], seed: 83, tailMax: 6 })
  ctx.globalCompositeOperation = 'source-over'
  // 足元の砂：後ろ足の後ろへ床をこすって伸びる。まだ保つ=短い / もう保たない=長く濃い
  const len = brk ? 1 : 0.45
  if (age < 0.55) {
    const grow = clamp01(age / 0.16)
    const fade = clamp01(1 - (age - 0.12) / 0.4)
    const n = brk ? 8 : 5
    for (let i = 0; i < n; i++) {
      const off = 4 + i * 12 * grow * len
      glow(ctx, footX - off, floorY - 4 - 3 * h01(i), (11 + 7 * h01(i * 3)) * (0.7 + 0.3 * len), '170,158,144', (brk ? 0.6 : 0.45) * fade, 1.8, 0.55)
    }
    ctx.globalCompositeOperation = 'lighter'
    embers(ctx, footX, floorY - 2, age, { n: brk ? 10 : 5, a0: -170 * DEG, a1: -120 * DEG, v0: 80, v1: 210, life: 0.4, grav: 1400, drag: 2,
      size: 1.4, cols: ['#a89c8c', '#8c8478'], seed: 19, tailMax: 4 })
  }
  ctx.restore()
}

/**
 * 受けた 10-06 (img)：刃は光らせず、暗い鋼色の火花 (素材) を相手側へ、砂ぼこり (素材) を後ろ足の後ろへ。
 * もう保たない (体幹 5 割超) は朱の火花 (暗め)・砂ぼこりが大きい。素材が読めていない間は a2 (コードの粒) で描く。
 */
export function drawGuardImpactImg(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, dir: number, postureRatio: number): void {
  if (age < 0 || age > 0.6) return
  if (!matReady('gspark_hold') || !matReady('gspark_strain') || !matReady('gdust')) { drawGuardImpactA2(ctx, x, y, age, dir, postureRatio); return }
  const brk = postureRatio > 0.5
  const px = x + guardImpactPushPx(age, dir, postureRatio)
  const cx = px + 12, cy = y - 40            // 剣と相手の得物の接点 (右向きで)
  const footX = px - 16, floorY = y + 60     // 後ろ足
  ctx.save()
  mirrorAbout(ctx, px, dir)
  drawSeqAt(ctx, brk ? 'gspark_strain' : 'gspark_hold', age, [0, 0.05, 0.15, 0.28], 0.44, cx + 3, cy, 30, 180, brk ? 0.32 : 0.38)
  // 砂ぼこりは少量 (2026-10-19・検収パック 1022 問1「ガードをした時には少量の砂ぼこり…出過ぎな気がします」・全ボス共通)。
  //   前の量 (大きさ 0.22/0.3・濃さ 0.65/0.8) は DEV の ?guardDust=old。
  const dustOld = guardDustOld()
  drawSeqAt(ctx, 'gdust', age, [0, 0.08, 0.22, 0.38], 0.6, footX, floorY, 355, 180,
    dustOld ? (brk ? 0.3 : 0.22) : (brk ? 0.2 : 0.14), dustOld ? (brk ? 0.8 : 0.65) : (brk ? 0.55 : 0.4))
  ctx.restore()
}

// ---------- 崩した瞬間 (parry_down) ----------
/** カメラの寄りと揺れ (世界の層だけ・ゲーム座標の px)。 */
export function parryDownCamera(age: number): { zoom: number; ox: number; oy: number } {
  if (age < 0 || age > 0.25) return { zoom: 1, ox: 0, oy: 0 }
  const zk = age < 0.03 ? age / 0.03 : Math.max(0, 1 - (age - 0.03) / 0.22)
  const sh = 5 * Math.max(0, 1 - age / 0.25)
  return { zoom: 1 + 0.07 * zk, ox: Math.sin(age * 173) * sh, oy: Math.cos(age * 131) * sh }
}

/** 接点の閃光 (素材の4コマ・金)。世界の層の中で描く。 */
export function drawParryDownFlash(ctx: CanvasRenderingContext2D, x: number, y: number, age: number): void {
  const f = MAT.flash_just_0
  if (!f || !f.naturalHeight) return
  const s = 170 / f.naturalHeight
  drawSeqAt(ctx, 'flash_just', age, [0, 0.03, 0.1, 0.19], 0.32, x, y, f.naturalWidth / 2, f.naturalHeight / 2, s)
}

/** 画面ごと金に染まる (止めの間は最大・明けてから 0.2 秒で戻る)。sx,sy = 接点の画面座標 (ゲーム座標)。HUD の下で描く。 */
export function drawParryDownTint(ctx: CanvasRenderingContext2D, w: number, h: number, sx: number, sy: number, age: number): void {
  if (age < 0 || age > 0.22) return
  const e = age < 0.02 ? 1 : Math.max(0, 1 - (age - 0.02) / 0.2)
  if (e <= 0.003) return
  ctx.save()
  ctx.globalCompositeOperation = 'soft-light'
  ctx.fillStyle = `rgba(255,190,70,${0.95 * e})`
  ctx.fillRect(0, 0, w, h)
  ctx.globalCompositeOperation = 'lighter'
  const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, w * 0.7)
  g.addColorStop(0, `rgba(255,200,100,${0.55 * e})`)
  g.addColorStop(1, `rgba(255,170,60,${0.16 * e})`)
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h)
  ctx.restore()
}
